import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  Cell,
  Rectangle,
} from 'recharts';
import { TranslationKeyEnum } from '../../types/constants';
import { useTranslation } from '../../hooks/useTranslation';
import { TimePeriod, ChartDataPoint, ChartSeries } from '../../types/insights';
import { EmptyMonthlyAdoptionsChart } from './EmptyMonthlyAdoptionsChart';
import {
  buildMonthlyAdoptionChartData,
  getYAxisConfig,
  isAdoptionsMonthView,
  MonthlyAdoptionPoint,
  getAdjustedBarPosition,
} from './utils/adoptionsChartUtils';
import { BoltIcon } from '../icons';
import { useShelterState } from '../../hooks/useShelter';
import { usePetParentReportActions } from '../../hooks/usePetParentReports';

// Determines whether a given month/year falls within the petszel-managed
// window for the current shelter (i.e. shelter is petszel-enabled and the
// month is at/after `petszel_first_used_at`). For these months, missing
// reports are not applicable since data is sourced automatically from
// petszel, so we should not display "--" placeholders.
const isPetszelManagedMonth = (
  shelter:
    | { is_petszel_enabled?: boolean | null; petszel_first_used_at?: string | null }
    | null
    | undefined,
  monthName: string | undefined,
  year: number | undefined
): boolean => {
  if (!shelter?.is_petszel_enabled || !shelter.petszel_first_used_at) return false;
  if (!monthName || typeof year !== 'number') return false;

  const firstUsed = new Date(shelter.petszel_first_used_at);
  if (Number.isNaN(firstUsed.getTime())) return false;

  const monthIdx = SHORT_MONTHS.indexOf(monthName);
  if (monthIdx === -1) return false;

  const firstUsedYear = firstUsed.getUTCFullYear();
  const firstUsedMonthIdx = firstUsed.getUTCMonth();

  return year > firstUsedYear || (year === firstUsedYear && monthIdx >= firstUsedMonthIdx);
};

const SHORT_MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

interface MonthlyBarChartProps {
  data: ChartDataPoint[];
  series: ChartSeries[];
  height?: number;
  className?: string;
  selectedPeriod?: TimePeriod;
  type?: string;
  hasOptInData?: boolean;
  hasAdoptionData?: boolean;
}

// Custom XAxis tick component
interface CustomTickProps {
  x?: number;
  y?: number;
  payload?: {
    value: string;
  };
  data: ChartDataPoint[];
  series: ChartSeries[];
  type?: string;
}

const CustomXAxisTick: React.FC<CustomTickProps> = ({ x, y, payload, data, type }) => {
  const { shelter } = useShelterState();

  if (!payload || !x || !y) return null;

  // Find the data point for this month
  const dataPoint = data.find((item) => item.month === payload.value);

  // Determine whether the bolt icon should be shown for this tick.
  // Only show for petszel-enabled shelters, and only for months at/after
  // the shelter's `petszel_first_used_at` date.
  const tickYear = typeof dataPoint?.year === 'number' ? dataPoint.year : undefined;
  const showBoltIcon = isPetszelManagedMonth(shelter, payload.value, tickYear);

  // Show red label if reporting window closed and report is missing
  const optIns = typeof dataPoint?.optIns === 'number' ? dataPoint.optIns : 0;
  const totalAdoptions =
    typeof dataPoint?.totalAdoptions === 'number' ? dataPoint.totalAdoptions : 0;

  const isPastMonth = !!dataPoint?.isPastMonth;
  const optInRate = dataPoint?.optInRate;
  const reportMissing = !!dataPoint?.reportMissing;
  const isMissedMonth = !!dataPoint?.isMissedMonth;
  let isRed = false;
  if (type === 'adoptions') {
    isRed = !!(dataPoint?.isPastMonth && dataPoint?.reportMissing === true);
  } else {
    isRed =
      isPastMonth &&
      // Petszel missed month: adoptions reported but 0 opt-ins tracked
      (isMissedMonth ||
        // opt-ins exist but adoption report shows 0 adoptions (report was submitted)
        (optIns >= 0 && totalAdoptions === 0 && reportMissing) || //1 - 0 -
        // optInRate is null and both counts are zero (covers both hasZeroOptIns true/false cases)
        (optInRate === null && optIns === 0 && totalAdoptions >= 0) ||
        // inconsistent: rate=0, optIns=0 but adoptions>0 and NOT hasZeroOptIns
        (!dataPoint?.hasZeroOptIns && optInRate === 0 && optIns === 0));
  }

  const textColor = isRed ? '#ef4444e3' : 'rgba(0, 0, 0, 0.3)';

  // Approximate text width to position the icon just to the left of the label
  const iconWidth = 10;
  const iconHeight = 12;
  const textWidth = (payload.value?.length ?? 0) * 7;
  const iconTextGap = 6;
  const iconX = -textWidth / 2 - iconWidth - iconTextGap;
  const iconY = 4;

  const labelX = showBoltIcon ? (iconWidth + iconTextGap) / 2 : 0;

  return (
    <g transform={`translate(${x},${y})`}>
      {showBoltIcon && (
        <g transform={`translate(${iconX + labelX}, ${iconY})`}>
          <BoltIcon height={iconHeight} width={iconWidth} fill={textColor} />
        </g>
      )}
      <text
        x={labelX}
        y={0}
        dy={16}
        textAnchor="middle"
        fill={textColor}
        fontSize={14}
        fontWeight={500}
      >
        {payload.value}
      </text>
    </g>
  );
};

// Custom tooltip component
interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    name: string;
    value: number;
    color: string;
    dataKey: string;
    payload: {
      month: string;
      optInRate: number;
      optIns: number;
      totalAdoptions: number;
      year: number;
      hasZeroOptIns: boolean;
      isPastMonth?: boolean;
      reportMissing?: boolean;
      isFirstReportDueMonth?: boolean;
      isZeroZeroState?: boolean;
      isMissedMonth?: boolean;
      name?: string;
      isSingleMonthView?: boolean;
      isCurrentMonth?: boolean;
      color?: string;
    };
  }>;
  label?: string;
  type?: string;
}

const AdoptionTooltip: React.FC<TooltipProps> = ({ active, payload, label }) => {
  const { t } = useTranslation();
  const { shelter } = useShelterState();
  if (active && payload?.length) {
    const tooltipData = payload[0]?.payload;
    // For petszel-managed months on petszel-enabled shelters, data is sourced
    // automatically so a "missing report" is not applicable. Treat such
    // months as having data so we don't render "--" placeholders.
    const isPetszelMonth = isPetszelManagedMonth(shelter, tooltipData?.month, tooltipData?.year);
    const isReportMissing = !isPetszelMonth && tooltipData?.reportMissing;
    // For single month view, the payload has 'name' property directly on payload object from our transformation
    const isSingleMonthView = payload[0].payload.isSingleMonthView;
    const headerLabel = isSingleMonthView ? payload[0].payload.month : label;

    const getTotalDisplayValue = (
      isReportMissing: boolean | undefined,
      isSingleMonthView: boolean | undefined,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      payload: any[]
    ) => {
      // 1. Check if data is missing entirely
      if (isReportMissing) {
        return '--';
      }

      // 2. Check the view type
      if (isSingleMonthView) {
        return payload[0]?.value;
      }

      // 3. Fallback to the totalAdoptions property or zero
      return payload[0]?.payload?.totalAdoptions ?? 0;
    };

    return (
      <div className="bg-gray-675 border border-black/20 rounded-lg shadow-lg p-3">
        <p className="text-sm font-medium text-white mb-2">
          {headerLabel} {payload[0].payload.year}
        </p>
        {payload.map((entry, index: number) => {
          const barColor = isSingleMonthView ? entry.payload?.color : entry.color;
          return (
            <p key={index} className="text-sm text-white">
              <span
                className="inline-block w-3 h-3 rounded-full mr-2"
                style={{ backgroundColor: barColor }}
              />
              {isSingleMonthView ? entry.payload.name : entry.name}:{' '}
              {isReportMissing ? '--' : entry.value}
            </p>
          );
        })}
        <hr className="mt-2 mb-2 opacity-20" />
        <p className="text-sm font-medium text-white">
          {`${t(TranslationKeyEnum.Total)}: ${getTotalDisplayValue(isReportMissing, isSingleMonthView, payload)}`}
        </p>
      </div>
    );
  }
  return null;
};

const OptInChartTooltip: React.FC<TooltipProps> = ({ active, payload, label, type }) => {
  const { t } = useTranslation();
  const { shelter } = useShelterState();
  const { downloadOptinGuide } = usePetParentReportActions();
  if (active && payload?.length) {
    const tooltipData = payload[0]?.payload;
    // For petszel-managed months, data is sourced automatically; suppress
    // "--" placeholders by treating reports as present.
    const isPetszelMonth = isPetszelManagedMonth(shelter, tooltipData?.month, tooltipData?.year);
    const isReportMissing = !isPetszelMonth && tooltipData?.reportMissing;

    const getDisplayValue = (entry: (typeof payload)[0]) => {
      // For totalAdoptions, show '--' if adoption report is missing
      if (entry.dataKey === 'totalAdoptions' && isReportMissing) {
        return '--';
      }
      // For optIns:
      // - Show '--' if no pet parent report was submitted (value is 0 and hasZeroOptIns is false)
      // - Show 0 if a report was submitted with 0 opt-ins (hasZeroOptIns is true)
      // - Otherwise show the actual value
      if (entry.dataKey === 'optIns') {
        if (entry.payload.hasZeroOptIns) {
          return 0;
        }
        // If optIns is 0 but hasZeroOptIns is false, no report was submitted
        if (entry.value === 0) {
          // For petszel-managed months, treat absent report as 0.
          return isPetszelMonth ? 0 : '--';
        }
      }
      // Return the value if it's a number (including 0), otherwise '--'
      return typeof entry.value === 'number' ? entry.value : '--';
    };

    const getTooltipLabel = () => {
      const data = payload[0].payload;

      // 1. Handle Adoptions Type
      if (type === 'adoptions') {
        const value = isReportMissing ? '--' : (data.totalAdoptions ?? 0);
        return `${t(TranslationKeyEnum.Total)}: ${value}`;
      }

      // 2. Handle Opt-In Rate logic
      const { isFirstReportDueMonth, isZeroZeroState, optIns, hasZeroOptIns, optInRate } = data;

      // First report due month: calculation begins the following month
      if (isFirstReportDueMonth) {
        return t(TranslationKeyEnum.OptInRateNotAvailable);
      }

      // 0,0 State: No activity at shelter (0 adoptions AND 0 opt-ins)
      if (isZeroZeroState) {
        return t(TranslationKeyEnum.OptInRatePercentage, 0);
      }

      // Missed month: adoptions reported but 0 opt-ins tracked — show 0%
      if (data.isMissedMonth) {
        return t(TranslationKeyEnum.OptInRatePercentage, 0);
      }

      // Petszel shelter and current month: show opt-in %
      if (shelter?.is_petszel_enabled && data.isCurrentMonth) {
        return t(TranslationKeyEnum.OptInRatePercentage, optInRate ?? 0);
      }

      // Past months or future/current months without data: not applicable
      if (optIns === 0 && !hasZeroOptIns) {
        return  t(TranslationKeyEnum.OptInRateNotApplicable);
      }

      // Show calculated rate if available
      if (optInRate !== null) {
        return t(TranslationKeyEnum.OptInRatePercentage, optInRate);
      }

      // Default: not applicable
      return t(TranslationKeyEnum.OptInRateNotApplicable);
    };

    const isMissedMonthTooltip = !!(payload[0]?.payload?.isMissedMonth);

    return (
      <div
        className={`bg-gray-675 border border-black/20 rounded-lg shadow-lg p-3 pointer-events-auto ${isMissedMonthTooltip ? 'max-w-[320px]' : 'max-w-[220px]'}`}
        onMouseMove={(e) => e.stopPropagation()}
      >
        {isMissedMonthTooltip && (
          <div className="flex gap-2 mb-3 pb-2 border-b border-white/20">
            <BoltIcon width={12} height={14} fill="#ffffff" className="shrink-0 mt-0.5" />
            <p className="text-sm text-white leading-snug">
              {t(TranslationKeyEnum.MissedMonthWarningMessage)}{' '}
              <button
                onClick={() => downloadOptinGuide()}
                className="underline font-bold text-white inline"
              >
                {t(TranslationKeyEnum.HillsOptInGuide)}
              </button>
            </p>
          </div>
        )}
        <p className="text-sm font-bold text-white mb-2">
          {label} {payload[0]?.payload?.year}
        </p>
        {payload.map((entry, index: number) => (
          <p key={index} className="text-sm font-light text-white">
            <span
              className="inline-block w-3 h-3 rounded-full mr-2"
              style={{ backgroundColor: entry.color }}
            />
            {entry.name}: {getDisplayValue(entry)}
          </p>
        ))}
        <hr className="mt-2 mb-2 opacity-20" />
        <p className="text-sm font-medium text-white">{getTooltipLabel()}</p>
      </div>
    );
  }
  return null;
};

export const MonthlyBarChart: React.FC<MonthlyBarChartProps> = ({
  data,
  series,
  height = 314,
  className = '',
  selectedPeriod = '',
  type = '',
  hasAdoptionData = true,
  // hasOptInData is kept in props for consistency but not currently used
  // since opt-in charts don't have Month-specific empty state handling
}) => {
  const { shelter } = useShelterState();
  
  // Determine if this is a stacked chart (for adoptions) or grouped chart (for opt-ins)
  const isStacked = type === 'adoptions' && selectedPeriod !== TimePeriod.MONTH;
  const isMonthlyAdoptions = isAdoptionsMonthView(type, selectedPeriod);

  // Calculate max value to set default domain if data is empty
  const maxValue = Math.max(
    ...data.flatMap((item) =>
      isStacked
        ? [series.reduce((sum, s) => sum + (Number(item[s.key]) || 0), 0)]
        : series.map((s) => Number(item[s.key]) || 0)
    ),
    0
  );

  const isChartEmpty = maxValue === 0;
  const stackId = isStacked && !isChartEmpty ? 'stack' : undefined;

  // For monthly adoptions view, use adoption-specific data availability
  if (isMonthlyAdoptions && isChartEmpty) {
    // In Month view all bars represent the same single month, so one check covers all bars
    const isPetszelManaged = isPetszelManagedMonth(shelter, data[0]?.month, data[0]?.year);
    return (
      <EmptyMonthlyAdoptionsChart
        series={series}
        height={height}
        className={className}
        hasCompleteData={hasAdoptionData}
        isPetszelManaged={isPetszelManaged}
      />
    );
  }

  // Transform data for monthly adoptions view to show categories on X axis
  let chartData: Array<ChartDataPoint | MonthlyAdoptionPoint> = data;
  if (isMonthlyAdoptions) {
    chartData = buildMonthlyAdoptionChartData(data, series);
  }

  // Custom shape to enforce minimum visible height only when value >= 1
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderMinHeightBar = (props: any) => {
    const { value, height, y, x, width, fill, radius } = props;
    const { adjustedY, adjustedHeight } = getAdjustedBarPosition(value, height, y);

    return (
      <Rectangle
        x={x}
        y={adjustedY}
        width={width}
        height={adjustedHeight}
        fill={fill}
        radius={radius}
      />
    );
  };

  // Custom render function for bar labels to hide 0 values
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderCustomBarLabel = (dy: number) => (props: any) => {
    const { x, y, value, height } = props;
    if (!value) return <g />;

    const { adjustedY } = getAdjustedBarPosition(value, height, y);

    return (
      <text
        x={x + 5}
        y={adjustedY + 5}
        dy={dy}
        fill="#ffffff"
        fontSize={20}
        fontWeight={700}
        textAnchor="start"
      >
        {value}
      </text>
    );
  };

  // Cast to any to allow mixing numbers and 'auto' string in the domain tuple
  const { domain: yAxisDomain, ticks: yAxisTicks } = getYAxisConfig(
    isChartEmpty,
    isMonthlyAdoptions,
    maxValue
  );

  return (
    <div className={className} style={{ width: '100%', height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={chartData}
          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
          barCategoryGap={isMonthlyAdoptions ? '2%' : '10%'}
        >
          <CartesianGrid
            strokeDasharray="none"
            stroke="rgba(0, 0, 0, 0.2)"
            horizontal={true}
            vertical={false}
          />
          <XAxis
            dataKey={isMonthlyAdoptions ? 'name' : 'month'}
            axisLine={false}
            tickLine={false}
            tick={
              isMonthlyAdoptions ? (
                { fontSize: 14, fontWeight: 500, fill: 'rgba(0, 0, 0, 0.3)' }
              ) : (
                <CustomXAxisTick data={data} series={series} type={type} />
              )
            }
            interval={isMonthlyAdoptions ? 0 : undefined}
          />
          <YAxis
            type="number"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 14, fontWeight: 500, fill: 'rgba(0, 0, 0, 0.3)' }}
            domain={yAxisDomain}
            ticks={yAxisTicks}
            tickCount={isChartEmpty ? 5 : undefined}
            allowDecimals={false}
          />
          {isStacked ? (
            <Tooltip content={<AdoptionTooltip />} />
          ) : isMonthlyAdoptions ? (
            <Tooltip content={<AdoptionTooltip />} />
          ) : (
            <Tooltip content={<OptInChartTooltip type={type} />} />
          )}
          {isMonthlyAdoptions ? (
            <Bar
              dataKey="value"
              radius={[4, 4, 0, 0]}
              label={renderCustomBarLabel(15)}
              shape={renderMinHeightBar}
            >
              {(chartData as MonthlyAdoptionPoint[]).map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Bar>
          ) : (
            series.map((serie) => (
              <Bar
                key={serie.key}
                dataKey={serie.key}
                fill={serie.color}
                name={serie.label}
                stackId={stackId}
                radius={isStacked ? [0, 0, 0, 0] : [4, 4, 0, 0]}
                label={!isStacked && type === 'adoptions' ? renderCustomBarLabel(15) : undefined}
                shape={!isStacked && type === 'adoptions' ? renderMinHeightBar : undefined}
              />
            ))
          )}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default MonthlyBarChart;
